// Zero-Marginal-Cost ($0 Baseline) AI Engine for Guru's Workshop LLC
// Provides intelligent technical spec formalization for prospects and deep request evaluation for Admins.

export const formalizeProjectScope = async (rawInput, platforms = []) => {
  // Simulate rapid AI analysis latency (300-600ms) for professional micro-interaction feel
  await new Promise(res => setTimeout(res, 450));

  const text = (rawInput || '').trim();
  const lower = text.toLowerCase();

  // Keyword-based architectural domain heuristics
  const isEcommerce = lower.includes('shop') || lower.includes('store') || lower.includes('cart') || lower.includes('buy') || lower.includes('sell') || lower.includes('rental') || lower.includes('stripe');
  const isSocialOrCommunity = lower.includes('social') || lower.includes('community') || lower.includes('chat') || lower.includes('forum') || lower.includes('feed') || lower.includes('user');
  const isAIOrMedia = lower.includes('ai') || lower.includes('audio') || lower.includes('video') || lower.includes('voice') || lower.includes('speech') || lower.includes('transcri') || lower.includes('vision');
  const isEnterpriseOrPortal = lower.includes('portal') || lower.includes('dashboard') || lower.includes('b2b') || lower.includes('client') || lower.includes('admin') || lower.includes('crm') || lower.includes('logistics');

  // Title generation
  let generatedTitle = '';
  if (text.length > 5 && text.length < 50 && !text.includes('\n')) {
    generatedTitle = text.charAt(0).toUpperCase() + text.slice(1);
  } else if (isAIOrMedia) {
    generatedTitle = 'AI-Powered Digital Media & Intelligence Platform';
  } else if (isEcommerce) {
    generatedTitle = 'Next-Gen Transactional Marketplace & Client Portal';
  } else if (isSocialOrCommunity) {
    generatedTitle = 'High-Engagement Community Platform & Member Hub';
  } else if (isEnterpriseOrPortal) {
    generatedTitle = 'Enterprise Client Command Center & Workflow Engine';
  } else {
    generatedTitle = 'Bespoke Multi-Platform Software Application';
  }

  // User Flows & Architecture breakdown
  const features = [];
  if (isEcommerce) {
    features.push('Multi-tier catalog search with real-time inventory synchronization');
    features.push('Frictionless Stripe / Apple Pay checkout with digital receipt ledger');
    features.push('User account order history, invoice download, and return status tracking');
  } else if (isAIOrMedia) {
    features.push('High-throughput ephemeral media processing pipeline with zero persistent overhead');
    features.push('AI intelligence reasoning & structured summary extraction engine');
    features.push('One-tap multi-language export and biometric security protection');
  } else if (isSocialOrCommunity) {
    features.push('Verified member profiles, role-based permissions, and activity streams');
    features.push('Low-latency messaging/notifications via edge serverless pub-sub');
    features.push('Automated moderation and community governance rules');
  } else {
    features.push('Responsive, high-density client portal with unified role-based authentication');
    features.push('Real-time operational dashboard with fast search, filters, and metric summaries');
    features.push('Automated transactional email notifications and audit logging');
  }

  // Selected Deliverables formatting
  const platSummary = platforms.length > 0 ? platforms.join(' + ') : 'Responsive Web Application + Serverless Cloud';

  const formalizedVision = [
    `🎯 EXECUTIVE OBJECTIVE:`,
    `Build a production-grade, highly reliable platform: "${generatedTitle}". Designed for high-speed user experience, verified security, and permanent $0.00/month idle infrastructure costs.`,
    ``,
    `👤 TARGET USER WORKFLOWS & KEY CAPABILITIES:`,
    ...features.map(f => `  • ${f}`),
    `  • Comprehensive responsive user interface with sub-100ms navigation`,
    `  • Secure session token management & data protection`,
    ``,
    `⚡ ZERO-IDLE-COST CLOUD TOPOLOGY ($0 BASELINE):`,
    `  • Global Edge CDN delivery with automated SSL/TLS termination`,
    `  • Serverless auto-scaling microservices & API routing`,
    `  • On-demand relational / key-value database with zero idle compute fees`,
    ``,
    `🛡️ QUALITY & GOVERNANCE:`,
    `  • Target Platforms: ${platSummary}`,
    `  • 100% Client Code & Intellectual Property Ownership upon handover`,
    `  • 3-Stage Milestone Quality Assurance & SOW Protection`
  ].join('\n');

  return {
    suggestedTitle: generatedTitle,
    formalizedVision
  };
};

export const evaluateClientRequest = (project) => {
  if (!project) return null;

  const text = ((project.description || '') + ' ' + (project.title || '')).toLowerCase();
  const platforms = project.platforms || [project.category || 'Web Application'];

  // Complexity heuristic
  let complexity = 'Moderate';
  let estimatedHoursMin = 35;
  let estimatedHoursMax = 55;
  let recommendedWeeks = 4;
  let riskFactors = [];
  let smartQuestions = [];

  const isMultiPlatform = platforms.length >= 3 || platforms.some(p => p.includes('iOS') || p.includes('Android'));
  const hasAI = text.includes('ai') || text.includes('voice') || text.includes('speech') || text.includes('transcrib') || text.includes('vision');
  const hasPayments = text.includes('stripe') || text.includes('payment') || text.includes('subscri') || text.includes('checkout') || text.includes('escrow');
  const isVague = text.length < 40;

  if (isVague) {
    riskFactors.push('Scope description is concise; core user journeys and edge cases require clarification.');
    smartQuestions.push('Who are the primary target users and what is the single most critical action they take on day 1?');
    smartQuestions.push('Do you have existing brand assets, Figma wireframes, or reference apps you admire?');
  }

  if (hasPayments) {
    smartQuestions.push('What payment flows are required (one-time transactions, recurring monthly subscriptions, or escrow holds)?');
  }

  if (isMultiPlatform) {
    complexity = 'Enterprise';
    estimatedHoursMin = 50;
    estimatedHoursMax = 85;
    recommendedWeeks = 6;
    riskFactors.push('Multi-platform deployment requires synchronized API schemas and App Store submission lead time.');
    smartQuestions.push('Do you have an active Apple Developer Program and Google Play Console account configured?');
  } else if (!hasAI && !hasPayments && !isMultiPlatform) {
    complexity = 'Low / Streamlined';
    estimatedHoursMin = 25;
    estimatedHoursMax = 40;
    recommendedWeeks = 3;
  }

  if (hasAI) {
    riskFactors.push('AI features require ephemeral streaming inference and strict token rate-limit guards.');
    smartQuestions.push('What are the expected monthly active user volumes or processing request volumes?');
  }

  // Baseline architectural and security safeguards
  if (riskFactors.length === 0) {
    riskFactors.push('Ensure strict CORS, CSP, and least-privilege token verification on all public API routes.');
    riskFactors.push('Verify edge cache invalidation rules to prevent stale state across distributed global nodes.');
  }

  // Ensure at least 2 questions are ready if Admin needs more info
  if (smartQuestions.length < 2) {
    smartQuestions.push('What is your ideal launch deadline or target go-live date?');
    smartQuestions.push('Are there specific 3rd-party APIs, legacy databases, or tools we must integrate with?');
  }

  // Generate recommended deliverables
  const recommendedDeliverables = [
    `Complete Technical Architecture Specification & Schema Design for ${project.title || 'Client Project'}`,
    `Production Frontend Implementation (${platforms.slice(0, 2).join(' & ')}) with Responsive Mobile Polish`,
    `Serverless Cloud Infrastructure & Database Tier ($0.00/mo Idle Baseline Architecture)`,
    hasPayments ? `Secure Stripe / Payment Gateway Integration with Webhook Ledgers` : `Role-Based Authentication, Session Guard & Audit Trail`,
    `Staging Verification URL, Digital SOW Handover, and 30-Day Post-Launch SLA Warranty`
  ];

  const executiveSummary = `Guru's Workshop LLC will architect and deliver a high-velocity, production-ready system for "${project.title || 'Client Project'}". We engineer modular, zero-downtime platforms adhering to strict permanent $0/month idle infrastructure economics, 3-stage milestone quality controls, and 100% intellectual property ownership upon final deployment.`;

  return {
    complexity,
    complexityScore: complexity,
    estimatedHours: Math.round((estimatedHoursMin + estimatedHoursMax) / 2),
    estimatedHoursRange: {
      min: estimatedHoursMin,
      max: estimatedHoursMax,
      suggestedHours: Math.round((estimatedHoursMin + estimatedHoursMax) / 2)
    },
    recommendedWeeks,
    suggestedWeeklyTimeline: recommendedWeeks,
    riskFactors,
    smartQuestions,
    clarifyingQuestions: smartQuestions,
    recommendedDeliverables,
    executiveSummary,
    executiveSOWSummary: executiveSummary,
    recommendedRate: complexity === 'Enterprise' ? 125 : 110
  };
};
